import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, BladApi } from '../api/client';
import type { Rozliczenie as RozliczenieTyp } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Ladowanie, Plakietka, Pole, PoleTekstowe, Przycisk,
} from '../components/ui';
import { dataGodzina, ilosc } from '../lib/format';

interface WierszRozliczenia {
  zwrocono: string;
  zuzyto: string;
  uszkodzono: string;
  zagubiono: string;
}

const pusty = (): WierszRozliczenia => ({ zwrocono: '', zuzyto: '', uszkodzono: '', zagubiono: '' });
const naLiczbe = (v: string) => Number((v || '0').replace(',', '.')) || 0;

/**
 * Jeden ekran do rozliczenia zajęć: co wróciło, co zostało zużyte, co się
 * zepsuło albo zginęło. Zużycie nie zmienia stanu — materiał zszedł już przy
 * wydaniu — ale zapisujemy je, żeby dało się odróżnić zużycie dydaktyczne
 * od straty.
 */
export function Rozliczenie() {
  const { id } = useParams();
  const nawiguj = useNavigate();
  const qc = useQueryClient();
  const { maUprawnienie } = useAuth();
  const mozeRozliczac = maUprawnienie('ruch.zwrot');

  const [wiersze, setWiersze] = useState<Record<number, WierszRozliczenia>>({});
  const [uwagi, setUwagi] = useState('');
  const [blad, setBlad] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['rozliczenie', id],
    queryFn: () => api.get<RozliczenieTyp>(`/dokumenty/${id}/rozliczenie`),
    enabled: Boolean(id),
  });

  // Podpowiedź: wszystko, co jeszcze nie rozliczone, traktujemy wstępnie jako
  // zwrot. Technik poprawia tylko to, co faktycznie zeszło.
  useEffect(() => {
    if (!data) return;
    setWiersze(Object.fromEntries(data.pozycje.map((p) => [
      p.movementId,
      { ...pusty(), zwrocono: Number(p.doRozliczenia) > 0 ? p.doRozliczenia : '' },
    ])));
  }, [data]);

  const zapisz = useMutation({
    mutationFn: () =>
      api.post(`/dokumenty/${id}/zwrot`, {
        uwagi,
        pozycje: Object.entries(wiersze)
          .map(([movementId, w]) => ({
            movementId: Number(movementId),
            zwrocono: naLiczbe(w.zwrocono),
            zuzyto: naLiczbe(w.zuzyto),
            uszkodzono: naLiczbe(w.uszkodzono),
            zagubiono: naLiczbe(w.zagubiono),
          }))
          .filter((p) => p.zwrocono + p.zuzyto + p.uszkodzono + p.zagubiono > 0),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rozliczenie', id] });
      void qc.invalidateQueries({ queryKey: ['stany'] });
      void qc.invalidateQueries({ queryKey: ['dokumenty'] });
      setBlad(null);
    },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się zapisać rozliczenia.'),
  });

  if (isLoading || !data) return <Ladowanie />;

  const suma = (m: number) => {
    const w = wiersze[m] ?? pusty();
    return naLiczbe(w.zwrocono) + naLiczbe(w.zuzyto) + naLiczbe(w.uszkodzono) + naLiczbe(w.zagubiono);
  };
  const cokolwiek = data.pozycje.some((p) => suma(p.movementId) > 0);
  const przekroczenie = data.pozycje.find((p) => suma(p.movementId) > Number(p.doRozliczenia));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">
            Rozliczenie wydania {data.dokument.number}
          </h1>
          <p className="mt-1 text-sm text-kamien-500">
            {data.zajecia
              ? `${data.zajecia.nazwa}${data.zajecia.grupa ? `, grupa ${data.zajecia.grupa}` : ''} · ${data.zajecia.prowadzacy} · ${dataGodzina(data.zajecia.start)}`
              : data.cel?.name ?? 'Wydanie bez przypisanych zajęć'}
          </p>
        </div>
        <Przycisk wariant="cichy" onClick={() => nawiguj('/dokumenty')}>Wróć do dokumentów</Przycisk>
      </div>

      {data.rozliczone && (
        <Komunikat rodzaj="sukces" tytul="Wydanie rozliczone w całości">
          Wszystkie pozycje mają komplet rozstrzygnięć. Dalsze wpisy nie są potrzebne.
        </Komunikat>
      )}

      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      <Karta tytul="Co wydano, co wraca">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                <th className="px-4 py-3 font-medium">Pozycja</th>
                <th className="px-4 py-3 text-right font-medium">Wydano</th>
                <th className="px-4 py-3 text-right font-medium">Rozliczono</th>
                <th className="px-4 py-3 font-medium">Wraca na stan</th>
                <th className="px-4 py-3 font-medium">Zużyto</th>
                <th className="px-4 py-3 font-medium">Uszkodzono</th>
                <th className="px-4 py-3 font-medium">Zagubiono</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-200">
              {data.pozycje.map((p) => {
                const w = wiersze[p.movementId] ?? pusty();
                const zaDuzo = suma(p.movementId) > Number(p.doRozliczenia);
                const ustaw = (pole: keyof WierszRozliczenia, v: string) =>
                  setWiersze((s) => ({ ...s, [p.movementId]: { ...(s[p.movementId] ?? pusty()), [pole]: v } }));

                return (
                  <tr key={p.movementId} className={zaDuzo ? 'bg-red-50/40' : ''}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-kamien-500">{p.kod}</span>{' '}
                      <span className="text-kamien-900">{p.nazwa}</span>
                      {p.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                      {p.equipmentUnit && (
                        <span className="block font-mono text-xs text-kamien-500">{p.equipmentUnit.nrCsm}</span>
                      )}
                      {p.batch && (
                        <span className="block text-xs text-kamien-400">
                          partia {p.batch.lotNumber ?? p.batch.id}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-kamien-900">
                      {ilosc(p.wydano)} {p.jednostka}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {Number(p.doRozliczenia) === 0
                        ? <Plakietka ton="ok">komplet</Plakietka>
                        : <span className="text-kamien-600">zostało {ilosc(p.doRozliczenia)}</span>}
                    </td>
                    {(['zwrocono', 'zuzyto', 'uszkodzono', 'zagubiono'] as const).map((pole) => (
                      <td key={pole} className="px-2 py-2">
                        <input
                          inputMode="decimal"
                          disabled={!mozeRozliczac || Number(p.doRozliczenia) === 0}
                          value={w[pole]}
                          onChange={(e) => ustaw(pole, e.target.value)}
                          aria-label={`${pole} — ${p.nazwa}`}
                          className="min-h-10 w-24 rounded-md border border-kamien-300 bg-white px-2 text-right
                            text-sm disabled:bg-kamien-100 disabled:text-kamien-400"
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-kamien-200 px-5 py-4">
          {przekroczenie && (
            <Komunikat rodzaj="blad">
              Pozycja „{przekroczenie.nazwa}”: rozliczasz więcej, niż zostało do rozliczenia
              ({ilosc(przekroczenie.doRozliczenia)} {przekroczenie.jednostka}).
            </Komunikat>
          )}

          <PoleTekstowe etykieta="Uwagi do rozliczenia" value={uwagi}
            onChange={(e) => setUwagi(e.target.value)} />

          {mozeRozliczac ? (
            <div className="flex flex-wrap gap-3">
              <Przycisk wariant="glowny"
                disabled={zapisz.isPending || !cokolwiek || Boolean(przekroczenie)}
                onClick={() => zapisz.mutate()}>
                {zapisz.isPending ? 'Zapisywanie...' : 'Zatwierdź rozliczenie'}
              </Przycisk>
              <Przycisk wariant="cichy" onClick={() => nawiguj('/dokumenty')}>Anuluj</Przycisk>
            </div>
          ) : (
            <Komunikat rodzaj="uwaga">
              Twoja rola pozwala oglądać rozliczenie, ale nie zatwierdzać zwrotów.
            </Komunikat>
          )}
        </div>
      </Karta>

      <p className="text-sm text-kamien-500">
        Materiał wpisany jako zużyty nie wraca na stan — zszedł już przy wydaniu. Zapisujemy go,
        żeby odróżnić zużycie dydaktyczne od straty; po tym liczy się koszt materiałowy zajęć.
        Uszkodzony sprzęt wraca do magazynu ze zmienionym stanem technicznym, zagubiony nie wraca wcale.
      </p>
    </div>
  );
}

/** Skrót używany z listy dokumentów. */
export function PrzyciskRozliczenia({ documentId }: { documentId: number }) {
  const nawiguj = useNavigate();
  return (
    <Przycisk wariant="glowny" className="min-h-9 text-xs"
      onClick={() => nawiguj(`/rozliczenie/${documentId}`)}>
      Rozlicz zajęcia
    </Przycisk>
  );
}

/** Pole do samodzielnego użycia w innych ekranach. */
export const PoleIlosci = Pole;
